import { Tooltip } from "antd";
import { TFunction } from "i18next";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { IRagConfig, IRagRule, RagStatus } from "../../@types/Rag";
import { IOption, SelectDropdown } from "../../components/SelectDropdown";
import { SkeletonLoading } from "../../components/Skeleton";
import {
  useCreateRagConfig,
  useUpdateRagConfig,
} from "../../hooks/mutations/ragConfig";
import {
  getActiveHospitalGroup,
  useOwnHospitalGroup,
  useRagAvailableMeasurements,
  useRagConfigById,
  useRagConfigList,
} from "../../hooks/queries/ragConfig";
import {
  useAvailableWards,
  useHospitalGroupWards,
} from "../../hooks/queries/wards";
import { useCurrentUserRole } from "../../hooks/useCurrentUserRole";
import { useToast } from "../../hooks/useToast";
import { useWardLabel } from "../../hooks/useWardLabel";
import { BuilderLayout } from "../../layout/components/BuilderLayout/BuilderLayout";
import { mixpanelActions } from "../../utils/mixpanel";
import { RagRuleRow } from "./components/RagRuleRow";
import {
  AddRuleButton,
  EmptyRulesHint,
  FieldColumn,
  FieldLabel,
  FieldLabelRow,
  FieldRow,
  InnerGrayPanel,
  OuterCard,
  PageContainer,
  PlusSign,
  RequiredMark,
  RuleDivider,
  RulesContainer,
  SectionBlock,
  SectionCard,
  SectionTitle,
  SkeletonRow,
} from "./RagConfig.styles";
import { hasAnyError, RuleError, validateRules } from "./validation";

// EN-5191: built from `t` because the labels render. `value` stays the identity the form
// and the API use.
const buildStatusOptions = (t: TFunction): IOption[] => [
  { label: t("rag_status_published"), value: "published", key: "status-pub" },
  {
    label: t("rag_status_unpublished"),
    value: "unpublished",
    key: "status-unpub",
  },
];

interface IEditableRule extends IRagRule {
  localId: string;
}

function createBlankRule(measurementType = ""): IEditableRule {
  return {
    localId: uuidv4(),
    measurementType,
    sex: "Both",
    color: "#2E7D32",
    // Default to a clean [start, end) band (≥ … <), which stacks without
    // overlapping at the shared boundary.
    startComparator: "GreaterThanOrEqualTo",
    // NaN keeps the field type as `number` (matches IRagRule) while signalling
    // "empty". Validation rejects NaN and the number input renders "" via
    // Number.isNaN. Switching to `undefined` here would require widening the
    // shared IRagRule shape.
    startValue: Number.NaN,
    endComparator: "LessThan",
    endValue: Number.NaN,
    label: "",
  };
}

function attachLocalIds(rules: IRagRule[]): IEditableRule[] {
  return rules.map((r) => ({ ...r, localId: uuidv4() }));
}

function stripLocalId(r: IEditableRule, index: number): IRagRule {
  return {
    id: r.id,
    measurementType: r.measurementType,
    sex: r.sex,
    color: r.color,
    startComparator: r.startComparator,
    startValue: r.startValue,
    endComparator: r.endComparator,
    endValue: r.endValue,
    label: r.label,
    // 1-based position in the list; the backend uses it to order the rules.
    order: index + 1,
  };
}

// Parses the trailing comma-separated ward ids from the backend's free-text
// conflict message (e.g. "...: 12, 34"). Brittle — track EN-XXXX to add a
// structured `meta.wardIds` field on the error response and drop this.
function parseConflictWardIds(message: string | undefined): number[] {
  if (!message) return [];
  const m = message.match(/:\s*([\d,\s]+)$/);
  if (!m) return [];
  return m[1]
    .split(",")
    .map((s) => Number.parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n));
}

export function RagConfigPage() {
  const { t } = useTranslation();
  const statusOptions = useMemo(() => buildStatusOptions(t), [t]);
  const wardLabel = useWardLabel();
  const navigate = useNavigate();
  const { configId } = useParams();
  const [searchParams] = useSearchParams();
  const { canViewRagTab, isSuperUser } = useCurrentUserRole();
  const { showToast } = useToast();

  const isEditMode = !!configId;
  const queryHospitalGroupId = searchParams.get("hg");

  const { hospitalGroup: ownHospitalGroup } = useOwnHospitalGroup();
  const activeGroup = getActiveHospitalGroup();

  const {
    ragConfig,
    isLoading: isRagConfigLoading,
    isError: isRagConfigError,
  } = useRagConfigById(isEditMode ? configId : undefined);

  // Super users have access to multiple hospital groups, so they must pick
  // one explicitly via ?hg=. Falling back to ownHospitalGroup[0] would create
  // the config in whichever group happens to come first from the API.
  const fallbackGroupId = isSuperUser
    ? undefined
    : activeGroup?.id ?? ownHospitalGroup?.hospitalGroupId ?? undefined;
  const queryHospitalGroupIdParsed = queryHospitalGroupId
    ? Number.parseInt(queryHospitalGroupId, 10)
    : fallbackGroupId;
  const effectiveHospitalGroupId = isEditMode
    ? ragConfig?.cmsHospitalGroupId
    : queryHospitalGroupIdParsed;

  const createMutation = useCreateRagConfig(effectiveHospitalGroupId);
  const updateMutation = useUpdateRagConfig(configId);
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Load the configs list so we can lock wards already used by other
  // published configs.
  const { configs: allConfigs, isLoading: isConfigListLoading } =
    useRagConfigList(effectiveHospitalGroupId);

  const useGroupWards = isSuperUser && !!effectiveHospitalGroupId;
  const { availableWards, isWardsLoading: isAvailableWardsLoading } =
    useAvailableWards();
  const { hospitalGroupWards, isWardsLoading: isGroupWardsLoading } =
    useHospitalGroupWards(effectiveHospitalGroupId ?? 0, useGroupWards);
  const wardOptionsSource = useGroupWards
    ? hospitalGroupWards ?? []
    : availableWards ?? [];
  const isWardOptionsLoading = useGroupWards
    ? isGroupWardsLoading
    : isAvailableWardsLoading;

  const [wards, setWards] = useState<number[]>([]);
  const [status, setStatus] = useState<RagStatus>("published");
  const [rules, setRules] = useState<IEditableRule[]>([]);
  const [errors, setErrors] = useState<RuleError[]>([]);
  // Set once the config has been copied into the form state above.
  const [hasSeededForm, setHasSeededForm] = useState(false);
  // Latches true the first time everything the form needs is in hand. Latching matters: after this
  // the user can change the wards, which refetches the measurements — that must not throw the whole
  // page back to skeletons while they are mid-edit.
  const [isFormReady, setIsFormReady] = useState(false);

  // EN-5123: the measurements that can be banded are whatever the selected wards collect — the
  // UNION across them, so a measurement only one ward has is offered (patients in the other wards
  // then show "-" in that column). One query for the page; every rule row shares the options.
  const {
    measurements: availableMeasurements,
    isPending: isMeasurementsPending,
  } = useRagAvailableMeasurements(wards);

  const measurementOptions: IOption[] = useMemo(
    () =>
      availableMeasurements.map((m) => {
        const name = m.measurementTypeDisplayName || m.measurementType;
        return {
          label: m.unit ? `${name} (${m.unit})` : name,
          value: m.measurementType,
          key: `m-${m.measurementType}`,
        };
      }),
    [availableMeasurements]
  );

  // Set of ward ids that are taken by another *published* config (excluding
  // this one if editing). Used to disable those wards in the dropdown.
  const lockedWardLookup = useMemo(() => {
    const map = new Map<number, number>(); // wardId -> conflicting configNumber
    allConfigs.forEach((c) => {
      if (c.status !== "published") return;
      if (isEditMode && c.id === configId) return;
      c.wards.forEach((w) => map.set(w, c.configNumber));
    });
    return map;
  }, [allConfigs, configId, isEditMode]);

  const wardSelectOptions: IOption[] = useMemo(
    () =>
      wardOptionsSource.map((w) => {
        const lockedIn = lockedWardLookup.get(w.wardId);
        const disabled = typeof lockedIn === "number";
        return {
          label: w.wardName,
          value: w.wardId.toString(),
          key: `ward-${w.wardId}`,
          disabled,
          title: disabled
            ? t("rag_wardInUse", { wards: wardLabel.plural, config: lockedIn })
            : undefined,
        };
      }),
    [wardOptionsSource, lockedWardLookup, wardLabel.plural, t]
  );

  useEffect(() => {
    mixpanelActions.track(
      isEditMode
        ? "Visited Screen: RagConfigEdit"
        : "Visited Screen: RagConfigCreate"
    );
  }, [isEditMode]);

  useEffect(() => {
    let nextWards: number[] = [];
    let nextStatus: RagStatus = "published";
    let nextRules: IEditableRule[] = [];

    if (!isEditMode) {
      // Create mode — start with a blank rule for nicer UX. Its measurement stays empty until a
      // ward is picked, since the ward set is what decides the options.
      nextRules = [createBlankRule()];
    } else if (ragConfig) {
      nextWards = ragConfig.wards ?? [];
      nextStatus = ragConfig.status ?? "published";
      nextRules =
        ragConfig.rules.length > 0
          ? attachLocalIds(ragConfig.rules)
          : [createBlankRule()];
    } else {
      return; // still loading the config in edit mode
    }

    setWards(nextWards);
    setStatus(nextStatus);
    setRules(nextRules);
    setErrors([]);
    setHasSeededForm(true);
  }, [ragConfig, isEditMode]);

  // EN-5123: hold the skeletons until the form is genuinely usable — the config, the ward options and
  // the measurement options for that config's wards. Dropping the skeleton on the config alone showed
  // an empty ward select and empty "Column" dropdowns for as long as those two calls took.
  useEffect(() => {
    if (isFormReady) return;
    // A failed config load never seeds the form; show it empty rather than skeletons forever.
    if (!isRagConfigError) {
      // The config list decides which wards are locked, so it gates the ward select too.
      if (isWardOptionsLoading || isConfigListLoading) return;
      if (isEditMode && (isRagConfigLoading || !hasSeededForm)) return;
      // In edit mode this only settles once the query key has caught up with the config's wards.
      if (isMeasurementsPending) return;
    }

    setIsFormReady(true);
  }, [
    isFormReady,
    isRagConfigError,
    isWardOptionsLoading,
    isConfigListLoading,
    isEditMode,
    isRagConfigLoading,
    hasSeededForm,
    isMeasurementsPending,
  ]);

  // EN-5123: removing a ward can drop measurements from the available set. Clear those selections so
  // the user re-picks, instead of silently saving a band none of the wards collects (which the API
  // rejects with RagConfigMeasurementNotAvailableForWards). Guarded on the options having actually
  // loaded for the current ward set — while the query is in flight the list is empty, and clearing
  // then would wipe valid rules.
  useEffect(() => {
    if (wards.length === 0 || measurementOptions.length === 0) return;

    const available = new Set(measurementOptions.map((o) => o.value));
    setRules((prev) => {
      const stale = (r: IEditableRule) =>
        !!r.measurementType && !available.has(r.measurementType);
      if (!prev.some(stale)) return prev;
      return prev.map((r) => (stale(r) ? { ...r, measurementType: "" } : r));
    });
  }, [wards, measurementOptions]);

  const updateRule = (localId: string, updates: Partial<IRagRule>) => {
    setRules((prev) =>
      prev.map((r) => (r.localId === localId ? { ...r, ...updates } : r))
    );
  };

  const deleteRule = (localId: string) => {
    setRules((prev) => prev.filter((r) => r.localId !== localId));
  };

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      createBlankRule(measurementOptions[0]?.value ?? ""),
    ]);
  };

  const buildPayload = (): IRagConfig | null => {
    if (!effectiveHospitalGroupId) return null;
    return {
      id: ragConfig?.id ?? null,
      cmsHospitalGroupId: effectiveHospitalGroupId,
      status,
      wards,
      rules: rules.map((r, i) => stripLocalId(r, i)),
    };
  };

  const handleConflictResponse = (
    response: { errors?: { code?: string; message?: string }[] } | undefined
  ): boolean => {
    const conflict = response?.errors?.find(
      (e) => e.code === "RagConfigPublishedWardConflict"
    );
    if (!conflict) return false;
    const ids = parseConflictWardIds(conflict.message);
    const names = ids
      .map((id) => wardOptionsSource.find((w) => w.wardId === id))
      .filter((w): w is { wardId: number; wardName: string } => !!w)
      .map((w) => w.wardName);
    const list = names.length > 0 ? names.join(", ") : conflict.message ?? "";
    showToast(
      t("rag_wardsPublishedElsewhere", {
        wards: wardLabel.pluralLower,
        configs: list,
      }),
      "error"
    );
    return true;
  };

  const handleSave = async () => {
    // The save button is disabled while these conditions hold; the early
    // return here is just a type-narrowing guard for buildPayload.
    if (!effectiveHospitalGroupId || wards.length === 0) return;

    const payload = buildPayload();
    if (!payload) return;

    const validationErrors = validateRules(
      payload.rules,
      new Set(measurementOptions.map((o) => o.value))
    );
    setErrors(validationErrors);

    if (hasAnyError(validationErrors)) {
      showToast(t("rag_toasts_fixErrors"), "error");
      return;
    }

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
      showToast(t("rag_toasts_saved"), "success");
      navigate("/rag");
    } catch (e) {
      const responseData = (e as { response?: { data?: unknown } })?.response
        ?.data;
      if (
        handleConflictResponse(
          responseData as
            | { errors?: { code?: string; message?: string }[] }
            | undefined
        )
      ) {
        return;
      }
      showToast(t("rag_toasts_saveFailed"), "error");
    }
  };

  const handleCancel = () => {
    navigate("/rag");
  };

  if (!canViewRagTab) {
    return <Navigate to="/patients/active" replace />;
  }

  const isHydrating = !isFormReady;
  const isSaveDisabled =
    !effectiveHospitalGroupId ||
    wards.length === 0 ||
    isHydrating ||
    isSubmitting;

  const renderDetailsSkeleton = () => (
    <SectionCard>
      <FieldRow>
        <FieldColumn>
          <FieldLabelRow>
            <FieldLabel>{wardLabel.plural}</FieldLabel>
            <RequiredMark>*</RequiredMark>
          </FieldLabelRow>
          <SkeletonLoading height={42} width={320} />
        </FieldColumn>

        <FieldColumn>
          <FieldLabel>{t("rag_status_label")}</FieldLabel>
          <SkeletonLoading height={42} width={148} />
        </FieldColumn>
      </FieldRow>
    </SectionCard>
  );

  const renderDetails = () => {
    if (isHydrating) return renderDetailsSkeleton();

    return (
      <SectionCard>
        <FieldRow>
          <FieldColumn>
            <FieldLabelRow>
              <FieldLabel>{wardLabel.plural}</FieldLabel>
              <RequiredMark>*</RequiredMark>
            </FieldLabelRow>
            <SelectDropdown
              options={wardSelectOptions}
              height={42}
              width={320}
              placeholder={t("rag_selectWardsPlaceholder", {
                ward: wardLabel.singularLower,
              })}
              multiple
              optionFilterProp="label"
              value={wards.map((id) => id.toString())}
              onValueChange={(v) => {
                const arr = Array.isArray(v) ? v : [v];
                const next = arr
                  .filter((s): s is string => Boolean(s))
                  .map((s) => Number.parseInt(s, 10));
                setWards(Array.from(new Set(next)));
              }}
              optionRender={(option) => {
                const data = option.data as IOption;
                const content = <span>{data.label}</span>;
                if (data.disabled && data.title) {
                  return (
                    <Tooltip
                      title={data.title}
                      placement="right"
                      mouseEnterDelay={0.1}
                    >
                      {content}
                    </Tooltip>
                  );
                }
                return content;
              }}
            />
          </FieldColumn>

          <FieldColumn>
            <FieldLabel>{t("rag_status_label")}</FieldLabel>
            <SelectDropdown
              options={statusOptions}
              height={42}
              width={148}
              value={status}
              onValueChange={(v) => setStatus(String(v) as RagStatus)}
            />
          </FieldColumn>
        </FieldRow>
      </SectionCard>
    );
  };

  const renderRagSetupSkeleton = () => (
    <SectionCard>
      <RulesContainer>
        {[0, 1, 2].map((i) => (
          <SkeletonRow key={i}>
            <SkeletonLoading height={42} width={189} />
            <SkeletonLoading height={42} width={88} />
            <SkeletonLoading height={42} width={140} />
            <SkeletonLoading height={42} width={88} />
            <SkeletonLoading height={42} width={88} />
            <SkeletonLoading height={42} width={180} />
          </SkeletonRow>
        ))}
      </RulesContainer>
    </SectionCard>
  );

  const renderRagSetup = () => {
    if (isHydrating) return renderRagSetupSkeleton();

    // EN-5123: the measurement options come from the selected wards, so there is nothing to
    // configure until at least one ward is picked.
    if (wards.length === 0) {
      return (
        <SectionCard>
          <EmptyRulesHint>
            {t("rag_selectWardsHint", { ward: wardLabel.singularLower })}
          </EmptyRulesHint>
        </SectionCard>
      );
    }

    if (rules.length === 0) {
      return (
        <SectionCard>
          <EmptyRulesHint>{t("rag_emptyRulesHint")}</EmptyRulesHint>
        </SectionCard>
      );
    }

    return (
      <SectionCard>
        <RulesContainer>
          {rules.map((rule, idx) => (
            <Fragment key={rule.localId}>
              {idx > 0 && <RuleDivider />}
              <RagRuleRow
                rule={rule}
                measurementOptions={measurementOptions}
                error={errors[idx] ?? {}}
                canDelete={rules.length > 1}
                onChange={(updates) => updateRule(rule.localId, updates)}
                onDelete={() => deleteRule(rule.localId)}
              />
            </Fragment>
          ))}
        </RulesContainer>
      </SectionCard>
    );
  };

  return (
    <BuilderLayout
      location="Rag"
      handleOnCancel={handleCancel}
      handleOnSave={handleSave}
      isWhiteButtonLoading={isSubmitting}
      isSaveDisabled={isSaveDisabled}
      lastEdited={ragConfig?.lastEditedDate ?? undefined}
      hasDashboardLayout
    >
      <PageContainer>
        <OuterCard>
          <InnerGrayPanel>
            <SectionBlock>
              <SectionTitle>{t("rag_sections_details")}</SectionTitle>
              {renderDetails()}
            </SectionBlock>

            <SectionBlock>
              <SectionTitle>{t("rag_sections_setup")}</SectionTitle>
              {renderRagSetup()}
            </SectionBlock>

            <AddRuleButton
              type="button"
              onClick={addRule}
              disabled={isHydrating || wards.length === 0}
            >
              <PlusSign>+</PlusSign>
              {t("rag_add")}
            </AddRuleButton>
          </InnerGrayPanel>
        </OuterCard>
      </PageContainer>
    </BuilderLayout>
  );
}
